import React, { useEffect, useCallback } from 'react';
import { X } from 'lucide-react';

type BaseModalProps = {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  title?: string;
  subtitle?: string;
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl';
  className?: string;
  showCloseButton?: boolean;
  closeOnClickOutside?: boolean;
  closeOnEscape?: boolean;
}

const maxWidthClasses = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  '2xl': 'max-w-2xl',
  '3xl': 'max-w-3xl',
  '4xl': 'max-w-4xl',
  '5xl': 'max-w-5xl',
};

export function BaseModal({
  isOpen,
  onClose,
  children,
  title,
  subtitle,
  maxWidth = '2xl',
  className = '',
  showCloseButton = true,
  closeOnClickOutside = true,
  closeOnEscape = true,
}: BaseModalProps) {
  // Handle ESC key press
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (closeOnEscape && e.key === 'Escape') {
        onClose();
      }
    },
    [closeOnEscape, onClose]
  );

  // Add ESC key listener
  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      // Lock body scroll when modal is open
      document.body.style.overflow = 'hidden';

      return () => {
        document.removeEventListener('keydown', handleKeyDown);
        // Restore body scroll
        document.body.style.overflow = 'unset';
      };
    }
  }, [isOpen, handleKeyDown]);

  // Handle click outside
  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (closeOnClickOutside && e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? 'modal-title' : undefined}
    >
      <div
        className={`bg-card border border-border rounded-lg shadow-lg w-full ${maxWidthClasses[maxWidth]} max-h-[90vh] flex flex-col overflow-hidden animate-in fade-in duration-200 ${className}`}
        onClick={e => e.stopPropagation()}
      >
        {/* Header - Sticky to stay accessible on mobile */}
        {(title || subtitle || showCloseButton) && (
          <div className="sticky top-0 z-10 flex items-start justify-between p-6 border-b border-border bg-card flex-shrink-0">
            <div className="flex-1">
              {title && (
                <h2 id="modal-title" className="text-2xl font-semibold text-foreground">
                  {title}
                </h2>
              )}
              {subtitle && <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>}
            </div>
            {showCloseButton && (
              <button
                onClick={onClose}
                className="ml-4 text-muted-foreground hover:text-foreground transition-colors flex-shrink-0"
                aria-label="Close modal"
              >
                <X className="h-6 w-6" />
              </button>
            )}
          </div>
        )}

        {/* Content - Scrollable container */}
        <div className="overflow-y-auto overflow-x-hidden p-6 flex-1">{children}</div>
      </div>
    </div>
  );
}
